/**
 * Single-flight de-dupe for token refreshes, aware of the `forceRefresh` mode.
 *
 * Concurrent callers normally share one in-flight refresh so simultaneous 401s
 * / proactive `getIdToken()` calls don't stampede Cognito. There is ONE
 * exception that must be honored for correctness:
 *
 *   A forced refresh must NEVER piggyback on an in-flight NON-forced refresh.
 *
 * `fetchAuthSession({ forceRefresh: false })` can hand back the very same cached
 * token that just triggered a 401. If a forced retry reused that in-flight
 * non-forced promise, it would re-send the stale token, 401 again, and log the
 * user out on what was a recoverable error — the exact failure this resilience
 * work is meant to prevent. So a forced request always starts its own refresh
 * unless an equally-or-more-forceful refresh is already in flight.
 *
 * Reuse rule (reuse the pending refresh only when it is at least as strong):
 *   - pending forced   + request forced/non-forced → reuse (fresh token is fine)
 *   - pending non-forced + request non-forced       → reuse
 *   - pending non-forced + request forced           → do NOT reuse; start forced
 */
export type RefreshFn = (forceRefresh?: boolean) => Promise<string | null>;

export function createRefreshDeduper(doRefresh: RefreshFn): RefreshFn {
  let inFlight: {
    promise: Promise<string | null>;
    forceRefresh: boolean;
  } | null = null;

  return (forceRefresh = false) => {
    const pending = inFlight;
    if (pending && (pending.forceRefresh || !forceRefresh)) {
      return pending.promise;
    }

    const promise = doRefresh(forceRefresh).finally(() => {
      // Only clear if this is still the current entry — a stronger forced
      // refresh may have superseded a non-forced one that is settling later.
      if (inFlight?.promise === promise) {
        inFlight = null;
      }
    });
    inFlight = { promise, forceRefresh };
    return promise;
  };
}
